import { IEvent } from './../../../models/IEvents';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IProduct } from '../../../models/IProduct';
import { IDish, IExtraDish } from '../../../types/dishes.types';
import { ICamp, ICartItem } from '../../../types/camp.types';
import { IBookingState } from '../../../screens/BookingDetail/types';
import { logout } from './AuthSlice';
import { IExtraActivity } from '../../../types/activities.types';

interface CartState {
  cartItems: ICartItem[];
  cartNumber: number;
}

const initialState: CartState = {
  cartItems: [],
  cartNumber: 0,
};

export const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    initCartItem: (state, action: PayloadAction<ICartItem>) => {
      state.cartItems = [...state.cartItems, action.payload];
      state.cartNumber = state.cartNumber + 1;
    },
    updateCartItemData: (
      state,
      action: PayloadAction<{
        id: string;
        data: Partial<IBookingState>;
        step: number;
      }>
    ) => {
      const result = state.cartItems.map((item) => {
        if (item.id === action.payload.id) {
          return {
            ...item,
            inputData: {
              ...item.inputData,
              ...action.payload.data,
            },
            step: action.payload.step,
          };
        } else {
          return {
            ...item,
            step: action.payload.step,
          };
        }
      });
      state.cartItems = result;
      state.cartNumber = result.reduce((preAmount, cartItem) => {
        const dishNum = cartItem.inputData.extra_dishes.reduce(
          (total, dish) => total + dish.quantity,
          0
        );
        const activityNum = (cartItem.inputData.extra_activities ?? []).reduce(
          (total, activity) => total + activity.attendees,
          0
        );
        let amount = 1 + dishNum + activityNum;
        return preAmount + amount;
      }, 0);
    },
    setInitCampBooking: (state, action: PayloadAction<ICamp>) => {},
    resetCart: (state, action: PayloadAction) => {},
    updateDishItems: (state, action: PayloadAction<IDish[]>) => {},
    addToCart: (state, action: PayloadAction<ICartItem>) => {},
    deleteCartDishItem: (
      state,
      action: PayloadAction<{ cartId: string; deleteDish: IExtraDish }>
    ) => {
      let cart = state.cartItems.find(
        (item) => item.id === action.payload.cartId
      );
      if (cart) {
        let dishes = cart.inputData.extra_dishes.filter(
          (item) =>
            !(
              item.dish.id === action.payload.deleteDish.dish.id &&
              item.date === action.payload.deleteDish.date &&
              item.type === action.payload.deleteDish.type
            )
        );
        cart = {
          ...cart,
          inputData: {
            ...cart.inputData,
            extra_dishes: dishes,
          },
        };
        const result = state.cartItems.map((item) => {
          if (item.id === cart?.id) {
            return cart;
          } else {
            return item;
          }
        });
        state.cartItems = result;
        state.cartNumber = result.reduce((preAmount, cartItem) => {
          const dishNum = cartItem.inputData.extra_dishes.reduce(
            (total, dish) => total + dish.quantity,
            0
          );
          const activityNum = cartItem.inputData.extra_activities.reduce(
            (total, activity) => total + activity.attendees,
            0
          );
          let amount = 1 + dishNum + activityNum;
          return preAmount + amount;
        }, 0);
      }
    },
    deleteCartActivityItem: (
      state,
      action: PayloadAction<{ cartId: string; deleteActivity: IExtraActivity }>
    ) => {
      let cart = state.cartItems.find(
        (item) => item.id === action.payload.cartId
      );
      if (cart) {
        let activities = cart.inputData.extra_activities.filter(
          (item) =>
            !(
              item.activity.id === action.payload.deleteActivity.activity.id &&
              item.date === action.payload.deleteActivity.date
            )
        );
        cart = {
          ...cart,
          inputData: {
            ...cart.inputData,
            extra_activities: activities,
          },
        };
        const result = state.cartItems.map((item) => {
          if (item.id === cart?.id) {
            return cart;
          } else {
            return item;
          }
        });
        state.cartItems = result;
        state.cartNumber = result.reduce((preAmount, cartItem) => {
          const dishNum = cartItem.inputData.extra_dishes.reduce(
            (total, dish) => total + dish.quantity,
            0
          );
          const activityNum = cartItem.inputData.extra_activities.reduce(
            (total, activity) => total + activity.attendees,
            0
          );
          let amount = 1 + dishNum + activityNum;
          return preAmount + amount;
        }, 0);
      }
    },
    deleteItem: (state, action: PayloadAction<string>) => {
      const result = state.cartItems.filter(
        (item) => item.id !== action.payload
      );
      state.cartItems = result;
      state.cartNumber = result.reduce((preAmount, dish) => {
        let amount = 1 + Number(dish.inputData.extra_dishes.length);
        return preAmount + amount;
      }, 0);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(logout, (state) => {
      // When the logout action is dispatched, clear the cart
      state.cartItems = [];
      state.cartNumber = 0;
    });
  },
});

export const {
  addToCart,
  deleteItem,
  deleteCartDishItem,
  setInitCampBooking,
  updateDishItems,
  resetCart,
  initCartItem,
  updateCartItemData,
  deleteCartActivityItem,
} = cartSlice.actions;
export default cartSlice.reducer;
